import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';

export interface NotificationPrefs {
  push: { readyForReview: boolean; chatMessages: boolean; reports: boolean; payments: boolean; newReviews: boolean };
  email: { monthlyReport: boolean; productUpdates: boolean };
  quietHours: { pauseAtNight: boolean; from: string; until: string };
}

export const defaultPrefs: NotificationPrefs = {
  push: { readyForReview: true, chatMessages: true, reports: true, payments: true, newReviews: false },
  email: { monthlyReport: true, productUpdates: false },
  quietHours: { pauseAtNight: true, from: '22:00', until: '08:00' },
};

interface State {
  prefs: NotificationPrefs;
  setPush: (key: keyof NotificationPrefs['push'], value: boolean) => void;
  setEmail: (key: keyof NotificationPrefs['email'], value: boolean) => void;
  setQuiet: (patch: Partial<NotificationPrefs['quietHours']>) => void;
}

// TODO(api): no notification-preferences endpoint exists yet, so these are remembered per browser only
// (localStorage). Move to the server (`PATCH /api/notifications/preferences`) when it lands.
export const useNotificationPrefs = create<State>()(
  persist(
    (set) => ({
      prefs: defaultPrefs,
      setPush: (key, value) => set((s) => ({ prefs: { ...s.prefs, push: { ...s.prefs.push, [key]: value } } })),
      setEmail: (key, value) => set((s) => ({ prefs: { ...s.prefs, email: { ...s.prefs.email, [key]: value } } })),
      setQuiet: (patch) => set((s) => ({ prefs: { ...s.prefs, quietHours: { ...s.prefs.quietHours, ...patch } } })),
    }),
    { name: 'mbm-notification-prefs', storage: createJSONStorage(() => localStorage) },
  ),
);

/** "22:00" -> "10:00 PM". */
export function formatClock(hhmm: string): string {
  const [h = '0', m = '00'] = hhmm.split(':');
  const hour = Number(h);
  return `${hour % 12 === 0 ? 12 : hour % 12}:${m} ${hour < 12 ? 'AM' : 'PM'}`;
}
